import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { X, ChevronLeft, ChevronRight, Pause, Play, Volume2, VolumeX, Heart, Send, Loader2, AlertCircle, RefreshCw, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API, API_BASE_URL } from '../config/api';

// Reaction emojis for stories
const STORY_REACTIONS = [
  { emoji: '❤️', name: 'heart' },
  { emoji: '🔥', name: 'fire' },
  { emoji: '😂', name: 'laugh' },
  { emoji: '😮', name: 'wow' },
  { emoji: '🙏', name: 'pray' },
  { emoji: '🎶', name: 'music' }
];



export default function StoriesViewerPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [reply, setReply] = useState('');
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchStories();
  }, [handle]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/stories/user/${handle}`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        const validStories = (data.stories || []).filter(story => {
          // Filter out expired stories and stories with invalid media
          const isExpired = story.expires_at && new Date(story.expires_at) < new Date();
          const hasMedia = story.media_url || story.media?.secure_url;
          return !isExpired && hasMedia && !story.expired;
        });
        
        if (validStories.length === 0) {
          toast.info('No active stories');
          navigate('/feed');
          return;
        }
        
        setStories(validStories);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Stories fetch error:', errorData);
        toast.error(errorData.detail || 'Stories not found');
        navigate('/feed');
      }
    } catch (err) {
      console.error('Stories fetch error:', err);
      toast.error('Failed to load stories');
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  };

  const currentStory = stories[currentIndex];
  
  // Get media URL - check both new and old format
  const getMediaUrl = useCallback((story) => {
    if (!story) return null;
    
    // New format with media object
    if (story.media?.secure_url) {
      return story.media.secure_url.startsWith('http') 
        ? story.media.secure_url 
        : `${API_BASE_URL}${story.media.secure_url}`;
    }
    
    // Old format with media_url
    if (story.media_url) {
      return story.media_url.startsWith('http') 
        ? story.media_url 
        : `${API_BASE_URL}${story.media_url}`;
    }
    
    return null;
  }, []);

  // Determine if current story is video
  const isVideo = currentStory?.media_type === 'video' || currentStory?.media?.resource_type === 'video';
  const mediaUrl = getMediaUrl(currentStory);
  const storyDuration = isVideo ? (currentStory?.duration || currentStory?.media?.duration || 15) * 1000 : 5000;

  // Reset media state when story changes
  useEffect(() => {
    setMediaLoading(true);
    setMediaError(null);
    setRetryCount(0);
  }, [currentIndex]);

  useEffect(() => {
    if (!currentStory || paused || mediaLoading || mediaError) return;

    // Reset progress
    setProgress(0);

    // Mark as viewed
    if (isAuthenticated) {
      fetch(`${API}/stories/${currentStory.story_id}/view`, {
        method: 'POST',
        headers: getAuthHeaders()
      }).catch((err) => {
        console.error('Failed to mark story as viewed:', err);
      });
    }

    // Start progress timer
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = (elapsed / storyDuration) * 100;
      
      if (newProgress >= 100) {
        goToNext();
      } else {
        setProgress(newProgress);
      }
    }, 50);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentIndex, paused, stories, mediaLoading, mediaError]);

  const goToNext = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      navigate('/feed');
    }
  }, [currentIndex, stories.length, navigate]);

  const goToPrev = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const togglePause = useCallback(() => {
    setPaused(prev => {
      const newPaused = !prev;
      if (videoRef.current) {
        if (newPaused) videoRef.current.pause();
        else videoRef.current.play().catch(() => {});
      }
      return newPaused;
    });
  }, []);

  const handleMediaLoad = useCallback(() => {
    setMediaLoading(false);
    setMediaError(null);
    console.log('Story media loaded successfully:', mediaUrl);
  }, [mediaUrl]);

  const handleMediaError = useCallback((e) => {
    console.error('Story media load error:', {
      storyId: currentStory?.story_id,
      mediaUrl: mediaUrl,
      error: e?.target?.error || 'Unknown error'
    });
    
    setMediaLoading(false);
    setMediaError({
      message: 'Failed to load media',
      url: mediaUrl,
      storyId: currentStory?.story_id
    });
  }, [currentStory, mediaUrl]);

  const handleRetry = useCallback(() => {
    if (retryCount >= 3) {
      toast.error('Media cannot be loaded. Skipping to next story.');
      goToNext();
      return;
    }
    
    setRetryCount(prev => prev + 1);
    setMediaLoading(true);
    setMediaError(null);
  }, [retryCount, goToNext]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !isAuthenticated) return;

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/stories/${currentStory.story_id}/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: reply })
      });
      
      if (res.ok) {
        toast.success('Reply sent!');
        setReply('');
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  const handleReaction = async (reactionName = 'heart') => {
    if (!isAuthenticated) return;

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await fetch(`${API}/stories/${currentStory.story_id}/react`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reaction: reactionName })
      });
      // Show the emoji briefly
      const emoji = STORY_REACTIONS.find(r => r.name === reactionName)?.emoji || '❤️';
      toast.success(emoji, { duration: 1000 });
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };

  const handleDeleteStory = async () => {
    if (!confirm('Delete this story?')) return;
    
    try {
      const res = await fetch(`${API}/stories/${currentStory.story_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Story deleted');
        goToNext();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete story');
      }
    } catch (err) {
      toast.error('Failed to delete story');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!currentStory) {
    navigate('/feed');
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" data-testid="stories-viewer">
      {/* Close button */}
      <button
        onClick={() => navigate('/feed')}
        className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted/60 transition-colors"
        aria-label="Close stories"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Progress bars */}
      <div className="absolute top-2 left-2 right-2 flex gap-1 z-30">
        {stories.map((_, idx) => (
          <div key={idx} className="flex-1 h-0.5 bg-muted/70 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-100"
              style={{
                width: idx < currentIndex ? '100%' : idx === currentIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* User info */}
      <div className="absolute top-8 left-4 z-30 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-muted/60 overflow-hidden flex items-center justify-center">
          {currentStory.user_avatar ? (
            <img 
              src={currentStory.user_avatar} 
              alt="" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<span class="font-bold text-white">${currentStory.user_name?.charAt(0) || '?'}</span>`;
              }}
            />
          ) : (
            <span className="font-bold text-white">
              {currentStory.user_name?.charAt(0) || '?'}
            </span>
          )}
        </div>
        <div className="text-white">
          <p className="font-medium text-sm">@{currentStory.user_handle}</p>
          <p className="text-xs text-muted-foreground/80">{getTimeAgo(currentStory.created_at)}</p>
        </div>
      </div>

      {/* Navigation arrows for desktop */}
      {currentIndex > 0 && (
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-25 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted/60 transition-colors hidden md:flex"
          aria-label="Previous story"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
      )}
      
      {currentIndex < stories.length - 1 && (
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-25 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted/60 transition-colors hidden md:flex"
          aria-label="Next story"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Navigation areas (touch/click) */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1/3 z-20 cursor-pointer"
        onClick={goToPrev}
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-1/3 z-20 cursor-pointer"
        onClick={goToNext}
      />
      <div
        className="absolute inset-0 z-10"
        onClick={togglePause}
      />

      {/* Story content */}
      <div className="w-full max-w-lg aspect-[9/16] relative flex items-center justify-center bg-background/50">
        {/* Loading state */}
        {mediaLoading && !mediaError && (
          <div className="absolute inset-0 flex items-center justify-center z-5">
            <Loader2 className="w-12 h-12 animate-spin text-muted-foreground/70" />
          </div>
        )}

        {/* Error state with retry */}
        {mediaError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-5 bg-background/80 p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <p className="text-foreground mb-2">Failed to load media</p>
            <p className="text-muted-foreground/70 text-sm mb-4">
              The story media couldn't be loaded. This may be due to a network issue or the media being unavailable.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleRetry}
                disabled={retryCount >= 3}
                className="bg-muted/50 border-border text-white hover:bg-muted/60"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry {retryCount > 0 ? `(${3 - retryCount} left)` : ''}
              </Button>
              <Button 
                onClick={goToNext}
                className="bg-secondary text-white"
              >
                Skip
              </Button>
            </div>
          </div>
        )}

        {/* Media content */}
        {mediaUrl && !mediaError && (
          isVideo ? (
            <video
              key={`${currentStory.story_id}-${retryCount}`}
              ref={videoRef}
              src={mediaUrl}
              className={`w-full h-full object-contain ${mediaLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
              autoPlay
              muted={muted}
              playsInline
              loop
              onLoadedData={handleMediaLoad}
              onCanPlay={handleMediaLoad}
              onError={handleMediaError}
              poster=""
            />
          ) : (
            <img
              key={`${currentStory.story_id}-${retryCount}`}
              src={mediaUrl}
              alt="Story"
              className={`w-full h-full object-contain ${mediaLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
              onLoad={handleMediaLoad}
              onError={handleMediaError}
            />
          )
        )}

        {/* Pause indicator */}
        {paused && !mediaLoading && !mediaError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
            <Pause className="w-16 h-16 text-foreground/80" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-4 left-4 right-4 z-30 space-y-3">
        {/* Reaction emoji bar */}
        {isAuthenticated && currentStory.user_id !== user?.user_id && (
          <div className="flex items-center justify-center gap-2 bg-black/40 rounded-full py-2 px-4 mx-auto w-fit">
            {STORY_REACTIONS.map((reaction) => (
              <button
                key={reaction.name}
                onClick={() => handleReaction(reaction.name)}
                className="w-10 h-10 rounded-full hover:bg-muted/60 flex items-center justify-center transition-all hover:scale-125"
                aria-label={`React with ${reaction.name}`}
              >
                <span className="text-2xl">{reaction.emoji}</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Reply input */}
        <div className="flex items-center gap-3">
          {isAuthenticated && currentStory.user_id !== user?.user_id ? (
            <form onSubmit={handleReply} className="flex-1 flex gap-2">
              <Input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Send a reply..."
                className="flex-1 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/70"
              />
              {reply && (
                <button
                  type="submit"
                  className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
                  aria-label="Send reply"
                >
                  <Send className="w-5 h-5 text-white" />
                </button>
              )}
            </form>
          ) : (
            <div className="flex-1" />
          )}

          {/* Delete button for story owner */}
          {currentStory.user_id === user?.user_id && (
            <button
              onClick={handleDeleteStory}
              className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center hover:bg-red-500/40 transition-colors"
              aria-label="Delete story"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
            </button>
          )}

          {isVideo && (
            <button
              onClick={() => setMuted(!muted)}
              className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted/60 transition-colors"
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
            </button>
          )}
        </div>
      </div>

      {/* View count (for story owner) */}
      {currentStory.user_id === user?.user_id && currentStory.view_count > 0 && (
        <div className="absolute bottom-20 left-4 text-sm text-muted-foreground/80">
          {currentStory.view_count} view{currentStory.view_count !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

function getTimeAgo(dateString) {
  if (!dateString) return '';
  
  try {
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    
    if (seconds < 0) return 'Just now';
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  } catch {
    return '';
  }
}
